import React from 'react';
import {Table, Button} from 'antd';
import type {TableColumnsType} from 'antd';
import {useTranslation} from 'react-i18next';
import {PodReviewType} from '../graphql/types/PodReviewType';
import {PODReviewStatus} from '../../../../enums/DeliveryEnum';

// 判断是否为超时数据（非人审通过且非审核误判）
const isTimeoutData = (record: PodReviewType): boolean => {
    // 如果是人审通过或审核误判，则不是超时数据
    if (record.podReviewStatus === PODReviewStatus.MANUAL_APPROVED ||
        record.podReviewStatus === PODReviewStatus.MANUAL_CORRECTED) {
        return false;
    }

    // 检查是否超过6小时时限
    if (record.firstPodUploadTime) {
        const uploadTime = new Date(record.firstPodUploadTime).getTime();
        const sixHoursAgo = Date.now() - (6 * 60 * 60 * 1000);
        return uploadTime < sixHoursAgo;
    }

    return false;
};

interface PodReviewTableProps {
    podReviewData: PodReviewType[];
    showOrderDetail: (record: PodReviewType) => void;
    onSelectChange: (selectedRowKeys: React.Key[], selectedRows: PodReviewType[]) => void;
    selectedRowKeys: React.Key[];
    loading: boolean;
    showActionColumn?: boolean; // 是否显示Action列
    isEditReviewButton?: boolean; // 是否显示"Edit Review"按钮
    showRowSelection?: boolean; // 是否显示行选择复选框
}

const PodReviewTable: React.FC<PodReviewTableProps> = ({
                                                           podReviewData,
                                                           showOrderDetail,
                                                           onSelectChange,
                                                           selectedRowKeys,
                                                           loading,
                                                           showActionColumn = true,
                                                           isEditReviewButton = false,
                                                           showRowSelection = true,
                                                       }) => {
    const {t} = useTranslation();

    const getStatusText = (status?: number) => {
        switch (status) {
            case PODReviewStatus.NOT_REVIEWED:
                return t('enum.podReviewStatus.NOT_REVIEWED');
            case PODReviewStatus.MACHINE_APPROVED:
                return t('enum.podReviewStatus.MACHINE_APPROVED');
            case PODReviewStatus.MACHINE_DISAPPROVED:
                return t('enum.podReviewStatus.MACHINE_DISAPPROVED');
            case PODReviewStatus.MANUAL_PENDING:
                return t('enum.podReviewStatus.MANUAL_PENDING');
            case PODReviewStatus.MANUAL_APPROVED:
                return t('enum.podReviewStatus.MANUAL_APPROVED');
            case PODReviewStatus.MANUAL_DISAPPROVED:
                return t('enum.podReviewStatus.MANUAL_DISAPPROVED');
            case PODReviewStatus.MANUAL_CORRECTED:
                //特殊处理 人审误判修正视作人审通过  展示也作为人审通过展示
                return t('enum.podReviewStatus.MANUAL_APPROVED');
            default:
                return t('enum.podReviewStatus.UNKNOWN');
        }
    };

    const columns: TableColumnsType<PodReviewType> = [
        {
            title: t('podReview.table.trkNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            align: 'left' as const,
            width: 140,
            ellipsis: true,
            render: (text: string, record: PodReviewType) => {
                const isTimeout = isTimeoutData(record);
                return (
                    <Button
                        type="link"
                        className={isTimeout ? 'text-red-600' : 'text-purple-600'}
                        onClick={() => showOrderDetail(record)}
                        style={isTimeout ? {paddingLeft: 0, color: '#dc2626', whiteSpace: 'nowrap'} : {paddingLeft: 0, whiteSpace: 'nowrap'}}
                    >
                        {text}
                    </Button>
                );
            },
        },
        {
            title: t('podReview.table.channelOrderNumber'),
            dataIndex: 'channelOrderNumber',
            key: 'channelOrderNumber',
            align: 'left' as const,
            width: 140,
            ellipsis: true,
            render: (text?: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '--'}</span>,
        },
        {
            title: t('podReview.table.client'),
            dataIndex: 'client',
            key: 'client',
            align: 'left' as const,
            width: 120,
            ellipsis: true,
            render: (client: any) => <span style={{ whiteSpace: 'nowrap' }}>{client?.channelName || '--'}</span>,
        },
        {
            title: t('podReview.table.hub'),
            dataIndex: 'hub',
            key: 'hub',
            align: 'left' as const,
            width: 120,
            ellipsis: true,
            render: (hub: any) => <span style={{ whiteSpace: 'nowrap' }}>{hub?.name || hub?.sortingCode || '--'}</span>,
        },
        {
            title: t('podReview.table.whs'),
            dataIndex: 'whs',
            key: 'whs',
            align: 'left' as const,
            width: 120,
            ellipsis: true,
            render: (whs: any) => <span style={{ whiteSpace: 'nowrap' }}>{whs?.name || whs?.sortingCode || '--'}</span>,
        },
        {
            title: t('podReview.table.area'),
            dataIndex: 'area',
            key: 'area',
            align: 'left' as const,
            width: 140,
            ellipsis: true,
            render: (area: any) => {
                const text = area?.name && area?.sortingCode
                    ? `${area.name} (${area.sortingCode})`
                    : area?.name || area?.sortingCode || '--';
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('podReview.table.dsp'),
            dataIndex: 'dsp',
            key: 'dsp',
            align: 'left' as const,
            width: 120,
            ellipsis: true,
            render: (dsp: any) => <span style={{ whiteSpace: 'nowrap' }}>{dsp?.name || dsp?.dspCode || '--'}</span>,
        },
        {
            title: t('podReview.table.driver'),
            dataIndex: 'driver',
            key: 'driver',
            align: 'left' as const,
            width: 140,
            ellipsis: true,
            render: (driver: any) => (
                <div>
                    <div style={{ whiteSpace: 'nowrap' }}>{driver?.name || '--'}</div>
                    {driver?.phone && (
                        <div className="text-sm text-gray-500" style={{ whiteSpace: 'nowrap' }}>{driver.phone}</div>
                    )}
                </div>
            ),
        },
        {
            title: t('podReview.table.podReviewStatus'),
            dataIndex: 'podReviewStatus',
            key: 'podReviewStatus',
            align: 'left' as const,
            width: 140,
            ellipsis: true,
            render: (status?: number) => <span style={{ whiteSpace: 'nowrap' }}>{getStatusText(status)}</span>,
        },
        {
            title: t('podReview.table.podStatusUpdatedAt'),
            dataIndex: 'podStatusUpdatedAt',
            key: 'podStatusUpdatedAt',
            align: 'left' as const,
            width: 180,
            ellipsis: true,
            render: (text?: string) => <span style={{ whiteSpace: 'nowrap' }}>{text ? new Date(text).toLocaleString() : '--'}</span>,
        },
        {
            title: t('podReview.table.firstPodUploadTime'),
            dataIndex: 'firstPodUploadTime',
            key: 'firstPodUploadTime',
            align: 'left' as const,
            width: 180,
            ellipsis: true,
            render: (text?: string) => <span style={{ whiteSpace: 'nowrap' }}>{text ? new Date(text).toLocaleString() : '--'}</span>,
        },
        {
            title: t('podReview.table.reviewer'),
            dataIndex: 'reviewer',
            key: 'reviewer',
            align: 'left' as const,
            width: 140,
            ellipsis: true,
            render: (reviewer: any) => (
                <div>
                    <div style={{ whiteSpace: 'nowrap' }}>{reviewer?.name || '--'}</div>
                    {reviewer?.phone && (
                        <div className="text-sm text-gray-500" style={{ whiteSpace: 'nowrap' }}>{reviewer.phone}</div>
                    )}
                </div>
            ),
        },
        {
            title: t('podReview.table.reviewNote'),
            dataIndex: 'podReviewNote',
            key: 'podReviewNote',
            align: 'left' as const,
            width: 150,
            ellipsis: true,
            render: (text?: string) => <span>{text || '--'}</span>,
        },
        {
            title: t('podReview.table.reviewTime'),
            dataIndex: 'reviewTime',
            key: 'reviewTime',
            align: 'left' as const,
            width: 180,
            ellipsis: true,
            render: (text?: string) => <span style={{ whiteSpace: 'nowrap' }}>{text ? new Date(text).toLocaleString() : '--'}</span>,
        },
        ...(showActionColumn ? [{
            title: t('podReview.table.action'),
            key: 'action',
            align: 'left' as const,
            width: 120,
            ellipsis: true,
            render: (_: any, record: PodReviewType) => {
                const isTimeout = isTimeoutData(record);
                return (
                    <Button
                        type="link"
                        className={`p-0 ${isTimeout ? 'text-red-600' : 'text-purple-600'}`}
                        onClick={() => showOrderDetail(record)}
                        style={isTimeout ? {color: '#dc2626', whiteSpace: 'nowrap'} : {whiteSpace: 'nowrap'}}
                    >
                        {isEditReviewButton ? t('podReview.actions.editReview') : t('podReview.actions.review')}
                    </Button>
                );
            },
        }] : []),
    ];

    const rowSelection = showRowSelection ? {
        selectedRowKeys,
        onChange: onSelectChange,
    } : undefined;

    return (
        <Table
            columns={columns}
            dataSource={podReviewData}
            rowKey="id"
            loading={loading}
            rowSelection={rowSelection}
            scroll={{x: 'max-content'}}
            pagination={false}
            size="middle"
            className="mb-4"
            tableLayout="fixed"
        />
    );
};

export default PodReviewTable;
